import React from 'react';
import { jsx, InterpolationWithTheme } from '@emotion/core';
import { isolateCss, stylesResetAttribute } from './isolateCss';

export type InterpolationWithTheme<T> = InterpolationWithTheme<T>;

// Function and `class` components will be functions, whereas
// HTML elements like `div` will be strings.
// TODO: Enumerate what possible "types" can be passed in.
const isComponent = (type: any) => {
  return typeof type === 'function';
};

const isFragment = (type: any) => {
  return typeof type === 'symbol';
};

const svgNodes = new Set([
  'path',
  'rect',
  'ellipse',
  'circle',
  'g',
  'defs',
  'clipPath',
  'linearGradient',
  'stop',
  'animate',
]);

const isSvgNode = (type: any) => {
  return svgNodes.has(type);
};

type PropsWithCSS = {
  css?: InterpolationWithTheme<any>;
  'data-css-reset'?: boolean;
};

/**
 * What I actually want to type this as
 *   const isolateComponent = (
 *     type: Parameters<typeof createElement>[0],
 *     props: Parameters<typeof createElement>[1] & PropsWithCSS,
 *     ...children: React.ReactNode[]
 *   ) => {
 * But alas, the flow typing for this is generated funky.
 * Refactor later when completely off flow.
 *
 * This is typed to return "any" because `DetaileDReactHTMLElement` doesn't
 * have an appropriate equivalent in our Flow React definition file
 */
const isolateComponent = <T extends PropsWithCSS | null>(
  type: any,
  props: T,
  ...children: Array<React.ReactNode>
): any => {
  if (!type) {
    return jsx(type, props, ...children);
  }

  // Don't add `stylesResetAttribute` to components since
  // they won't accept `stylesResetAttribute` as a prop.
  // Also don't add to SVG subnodes since resetting them
  // can unset some of their attributes.
  // Only add it to HTML elements.
  if (!isComponent(type) && !isFragment(type) && !isSvgNode(type)) {
    props = { ...props, [stylesResetAttribute]: true };
  }

  // If there's a `css` prop then `isolateCss` it to
  // ensure its specificity is high enough to overtake
  // the reset styles.
  if (props && props.css) {
    props.css = isolateCss(props.css);
  }

  return jsx(type, props, ...children);
};

export default isolateComponent;
